import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Truck, Loader2, CheckCircle, Building2 } from "lucide-react";

const CATEGORIES = [
  { value: "AGRO BASED", label: "कृषि आधारित (Agro Based)" },
  { value: "SERVICE", label: "सेवा उद्योग (Service)" },
  { value: "POLYMER AND CHEMICAL", label: "रासायनिक (Chemical)" },
  { value: "MINERAL BASED", label: "खनिज आधारित (Mineral)" },
  { value: "FOREST BASED", label: "वन आधारित (Forest)" },
  { value: "RURAL ENGINEERING", label: "ग्रामीण इंजीनियरिंग (Rural Eng.)" },
  { value: "TEXTILE", label: "वस्त्र उद्योग (Textile)" },
];

const SUPPLIER_TYPES = [
  { value: "Machinery", label: "मशीनरी सप्लायर" },
  { value: "Raw Material", label: "कच्चा माल सप्लायर" },
  { value: "Both", label: "दोनों (मशीनरी + कच्चा माल)" },
];

const STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Delhi", "Jammu and Kashmir", "Ladakh"
];

export default function SupplierRegistrationPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    business_name: "",
    business_name_hindi: "",
    owner_name: "",
    email: "",
    phone: "",
    alternate_phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    supplier_type: "",
    products_services: "",
    products_services_hindi: "",
    gst_number: "",
    website: "",
    experience_years: ""
  });

  const handleCategoryToggle = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedCategories.length === 0) {
      toast.error("कम से कम एक श्रेणी चुनें");
      return;
    }
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        categories: selectedCategories,
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null
      };
      
      await axios.post(`${API}/suppliers/register`, payload);
      setSuccess(true);
      toast.success("रजिस्ट्रेशन सफल!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "रजिस्ट्रेशन में त्रुटि");
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 py-12">
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="font-rajdhani font-bold text-3xl text-gray-800 mb-4">
              रजिस्ट्रेशन सफल!
            </h1>
            <p className="text-gray-500 mb-6">
              धन्यवाद! आपका रजिस्ट्रेशन हो गया है। Admin verification के बाद आपकी लिस्टिंग 
              उद्यमी मित्र पर दिखेगी और उद्यमी आपसे संपर्क कर सकेंगे।
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={() => navigate("/")} className="btn-secondary rounded-sm">
                होम पेज जाएं
              </Button>
              <Button onClick={() => navigate("/suppliers")} className="btn-primary rounded-sm">
                सप्लायर्स देखें
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
            <Truck className="w-6 h-6 text-orange-500" />
          </div>
          <div>
            <h1 className="font-rajdhani font-bold text-2xl text-gray-800">
              सप्लायर रजिस्ट्रेशन
            </h1>
            <p className="text-sm text-gray-8000">
              अपना व्यवसाय उद्यमी मित्र पर लिस्ट करें और नए ग्राहक पाएं
            </p>
          </div>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white/50 border border-gray-200 rounded-lg p-4">
            <Building2 className="w-6 h-6 text-orange-500 mb-2" />
            <h3 className="font-semibold text-gray-700 text-sm">नए उद्यमियों से जुड़ें</h3>
            <p className="text-xs text-gray-8000">हजारों नए व्यापारी आपसे संपर्क कर सकते हैं</p>
          </div>
          <div className="bg-white/50 border border-gray-200 rounded-lg p-4">
            <Truck className="w-6 h-6 text-emerald-500 mb-2" />
            <h3 className="font-semibold text-gray-700 text-sm">मुफ्त लिस्टिंग</h3>
            <p className="text-xs text-gray-8000">रजिस्ट्रेशन बिल्कुल मुफ्त है</p>
          </div>
          <div className="bg-white/50 border border-gray-200 rounded-lg p-4">
            <CheckCircle className="w-6 h-6 text-blue-500 mb-2" />
            <h3 className="font-semibold text-gray-700 text-sm">Verified Badge</h3>
            <p className="text-xs text-gray-8000">Admin verification से विश्वसनीयता बढ़ेगी</p>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="space-y-6">
            {/* Business Info */}
            <div>
              <h3 className="font-rajdhani font-bold text-lg text-gray-800 mb-4 border-b border-gray-200 pb-2">
                व्यवसाय की जानकारी
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">व्यवसाय का नाम (English) *</Label>
                  <Input
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    placeholder="ABC Machinery Suppliers"
                    className="input-industrial mt-1"
                    required
                    data-testid="business-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-500">व्यवसाय का नाम (हिंदी)</Label>
                  <Input
                    value={formData.business_name_hindi}
                    onChange={(e) => setFormData({ ...formData, business_name_hindi: e.target.value })}
                    placeholder="एबीसी मशीनरी सप्लायर्स"
                    className="input-industrial mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-500">मालिक/संपर्क व्यक्ति का नाम *</Label>
                  <Input
                    value={formData.owner_name}
                    onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                    placeholder="राजेश कुमार"
                    className="input-industrial mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-500">सप्लायर टाइप *</Label>
                  <Select
                    value={formData.supplier_type}
                    onValueChange={(value) => setFormData({ ...formData, supplier_type: value })}
                  >
                    <SelectTrigger className="input-industrial mt-1" data-testid="supplier-type-select">
                      <SelectValue placeholder="चुनें..." />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {SUPPLIER_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Categories */}
            <div>
              <h3 className="font-rajdhani font-bold text-lg text-gray-800 mb-4 border-b border-gray-200 pb-2">
                श्रेणियां (कम से कम एक चुनें) *
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {CATEGORIES.map((cat) => (
                  <div
                    key={cat.value}
                    className={`flex items-center space-x-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedCategories.includes(cat.value)
                        ? "bg-orange-500/20 border-orange-500"
                        : "bg-gray-50 border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => handleCategoryToggle(cat.value)}
                  >
                    <Checkbox
                      checked={selectedCategories.includes(cat.value)}
                      onCheckedChange={() => handleCategoryToggle(cat.value)}
                      className="border-slate-600"
                    />
                    <label className="text-sm text-gray-600 cursor-pointer">
                      {cat.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="font-rajdhani font-bold text-lg text-gray-800 mb-4 border-b border-gray-200 pb-2">
                संपर्क जानकारी
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">ईमेल *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contact@business.com"
                    className="input-industrial mt-1"
                    required
                    data-testid="email-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-500">फोन नंबर *</Label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="9876543210"
                    className="input-industrial mt-1"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-500">वैकल्पिक फोन</Label>
                  <Input
                    type="tel"
                    value={formData.alternate_phone}
                    onChange={(e) => setFormData({ ...formData, alternate_phone: e.target.value })}
                    placeholder="9876543211"
                    className="input-industrial mt-1"
                  />
                </div>
                <div>
                  <Label className="text-gray-500">वेबसाइट</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.business.com"
                    className="input-industrial mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h3 className="font-rajdhani font-bold text-lg text-gray-800 mb-4 border-b border-gray-200 pb-2">
                पता
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-500">पूरा पता *</Label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="दुकान/कार्यालय का पूरा पता..."
                    className="input-industrial mt-1 min-h-[80px]"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-gray-500">शहर *</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      placeholder="जयपुर"
                      className="input-industrial mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-gray-500">राज्य *</Label>
                    <Select
                      value={formData.state}
                      onValueChange={(value) => setFormData({ ...formData, state: value })}
                    >
                      <SelectTrigger className="input-industrial mt-1">
                        <SelectValue placeholder="चुनें..." />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-gray-200 max-h-60">
                        {STATES.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-500">पिनकोड *</Label>
                    <Input
                      value={formData.pincode}
                      onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                      placeholder="302001"
                      className="input-industrial mt-1"
                      required
                      maxLength={6}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-500">अनुभव (वर्ष)</Label>
                    <Input
                      type="number"
                      value={formData.experience_years}
                      onChange={(e) => setFormData({ ...formData, experience_years: e.target.value })}
                      placeholder="5"
                      className="input-industrial mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Products/Services */}
            <div>
              <h3 className="font-rajdhani font-bold text-lg text-gray-800 mb-4 border-b border-gray-200 pb-2">
                उत्पाद/सेवाएं
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-gray-500">आप क्या सप्लाई करते हैं? (English) *</Label>
                  <Textarea
                    value={formData.products_services}
                    onChange={(e) => setFormData({ ...formData, products_services: e.target.value })}
                    placeholder="Describe your products/services, machinery types, raw materials you supply..."
                    className="input-industrial mt-1 min-h-[100px]"
                    required
                    data-testid="products-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-500">आप क्या सप्लाई करते हैं? (हिंदी)</Label>
                  <Textarea
                    value={formData.products_services_hindi}
                    onChange={(e) => setFormData({ ...formData, products_services_hindi: e.target.value })}
                    placeholder="अपने उत्पादों/सेवाओं का विवरण दें..."
                    className="input-industrial mt-1 min-h-[100px]"
                  />
                </div>
                <div>
                  <Label className="text-gray-500">GST नंबर (वैकल्पिक)</Label>
                  <Input
                    value={formData.gst_number}
                    onChange={(e) => setFormData({ ...formData, gst_number: e.target.value })}
                    placeholder="22AAAAA0000A1Z5"
                    className="input-industrial mt-1"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-primary rounded-sm text-lg py-6"
              data-testid="submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  रजिस्टर हो रहा है...
                </>
              ) : (
                <>
                  <Truck className="w-5 h-5 mr-2" />
                  सप्लायर रजिस्टर करें
                </>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
